import {observer} from "mobx-react";
import {
    NavChildItem,
    NavFlyoutList,
    NavFlyoutTitle,
    NavGroupHeader,
    NavItem,
    NavItemCompact,
    NavItemRow,
    NavList,
    NavMiniLabel,
    StyledBadge,
} from "../../styles/nav";
import LinksStore from "./links";
import {hasPermission} from "../../utils/hasPermission";
import {NavLink, useLocation} from "react-router-dom";
import {Collapse, Popover} from "antd";
import {Image} from "../../styles/image";
import AppStore from "../../app.store";
import React, {useEffect, useState} from "react";

const {Panel} = Collapse;

interface NavItemModel {
    label: string;
    route?: string;
    icon?: string;
    permission?: string;
    hidden?: boolean;
    children?: NavItemModel[];
    id?: string;
}

interface NavListProps {
    burgerOpen?: boolean;
    setOpen: (isOpen: boolean) => void;
    forceExpanded?: boolean;
}

const NavbarListView = observer(({burgerOpen, setOpen}: NavListProps) => {
    return (
        <NavList burgerOpen={burgerOpen}>
            <RenderNavItems setOpen={setOpen} forceExpanded={!!burgerOpen} />
        </NavList>
    );
});

const isChildActive = (pathname: string, route?: string) => {
    if (!route) return false;
    if (route === '/') return pathname === '/';
    return pathname === route || pathname.startsWith(`${route}/`);
};

const isGroupActive = (pathname: string, children: NavItemModel[]) =>
    children.some((child) => isChildActive(pathname, child.route));

const ChildLinks = ({
    children,
    isNavOpen,
    onNavigate,
}: {
    children: NavItemModel[];
    isNavOpen: boolean;
    onNavigate: () => void;
}) => (
    <>
        {children.map((child) => (
            <NavLink
                to={child.route!}
                key={child.id ?? child.route}
                id={`page/${child.id!}`}
                onClick={onNavigate}
                style={{textDecoration: 'none'}}
            >
                {({isActive}) => (
                    <NavChildItem isActive={isActive} isNavOpen={isNavOpen} id={`page/${child.id!}`}>
                        {child.label}
                    </NavChildItem>
                )}
            </NavLink>
        ))}
    </>
);

const NavLeaf = ({
    item,
    isNavOpen,
    onNavigate,
}: {
    item: NavItemModel;
    isNavOpen: boolean;
    onNavigate: () => void;
}) => (
    <li style={{listStyle: 'none'}}>
        <NavLink
            to={item.route!}
            id={`page/${item.id!}`}
            onClick={onNavigate}
            style={{textDecoration: 'none', display: 'block'}}
        >
            {({isActive}) =>
                isNavOpen ? (
                    <NavItem isActive={isActive} id={`page/${item.id!}`}>
                        <NavItemRow>
                            <Image src={item.icon} height="16px" extent="16px"/>
                            <span style={{fontSize: 14, lineHeight: 1.35}}>{item.label}</span>
                        </NavItemRow>
                    </NavItem>
                ) : (
                    <NavItemCompact isActive={isActive} id={`page/${item.id!}`}>
                        <Image src={item.icon} height="18px" extent="18px"/>
                        <NavMiniLabel>{item.label}</NavMiniLabel>
                    </NavItemCompact>
                )
            }
        </NavLink>
    </li>
);

const NavGroup = ({
    item,
    visibleChildren,
    isNavOpen,
    onNavigate,
    groupActive,
}: {
    item: NavItemModel;
    visibleChildren: NavItemModel[];
    isNavOpen: boolean;
    onNavigate: () => void;
    groupActive: boolean;
}) => {
    const {pathname} = useLocation();
    const [flyoutOpen, setFlyoutOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(groupActive);

    useEffect(() => {
        if (groupActive) {
            setIsOpen(true);
            return;
        }
        setIsOpen(false);
    }, [pathname, item.id, groupActive]);

    if (!isNavOpen) {
        return (
            <li style={{listStyle: 'none'}} id={`page/${item.id!}`}>
                <Popover
                    placement="rightTop"
                    trigger="click"
                    open={flyoutOpen}
                    onOpenChange={setFlyoutOpen}
                    overlayInnerStyle={{padding: 8}}
                    content={(
                        <NavFlyoutList>
                            <NavFlyoutTitle>{item.label}</NavFlyoutTitle>
                            <ChildLinks
                                children={visibleChildren}
                                isNavOpen={isNavOpen}
                                onNavigate={() => {
                                    setFlyoutOpen(false);
                                    onNavigate();
                                }}
                            />
                        </NavFlyoutList>
                    )}
                >
                    <NavItemCompact
                        isActive={groupActive}
                        id={`page/${item.id!}-trigger`}
                        role="button"
                        tabIndex={0}
                    >
                        <Image src={item.icon} height="18px" extent="18px"/>
                        <NavMiniLabel>{item.label}</NavMiniLabel>
                    </NavItemCompact>
                </Popover>
            </li>
        );
    }

    return (
        <li style={{listStyle: 'none'}} id={`page/${item.id!}`}>
            <StyledBadge isNavOpen={isNavOpen} isActive={groupActive}>
                <Collapse
                    ghost
                    expandIconPosition="end"
                    activeKey={isOpen ? [item.id!] : []}
                    onChange={(keys) => {
                        const nextKeys = Array.isArray(keys) ? keys : keys ? [keys] : [];
                        setIsOpen(nextKeys.includes(item.id!));
                    }}
                >
                    <Panel
                        key={item.id!}
                        id={`page/${item.id!}-panel`}
                        header={(
                            <NavGroupHeader>
                                <Image src={item.icon} height="16px" extent="16px"/>
                                <span>{item.label}</span>
                            </NavGroupHeader>
                        )}
                    >
                        <ChildLinks
                            children={visibleChildren}
                            isNavOpen={isNavOpen}
                            onNavigate={onNavigate}
                        />
                    </Panel>
                </Collapse>
            </StyledBadge>
        </li>
    );
};

export const RenderNavItems = observer(({setOpen, forceExpanded}: NavListProps) => {
    const {isNavOpen, permissions, businessSettings} = AppStore;
    const expanded = isNavOpen || !!forceExpanded;
    const {pathname} = useLocation();
    const {NavItemsReq, NavItems} = LinksStore;

    useEffect(() => {
        const controller = new AbortController();
        void NavItemsReq(controller.signal);
        return () => controller.abort();
    }, [NavItemsReq, businessSettings]);

    if (!permissions) {
        return null;
    }

    const handleLinkClick = () => {
        setOpen(false);
    };

    const hasVisibleChildren = (children?: NavItemModel[]): boolean => {
        if (!children?.length) return false;
        return children.some((child) => {
            if (child.hidden) return false;
            if (child.permission && !hasPermission(permissions, child.permission, 'READ')) return false;
            return true;
        });
    };

    const getVisibleChildren = (children?: NavItemModel[]): NavItemModel[] => {
        if (!children?.length) return [];
        return children
            .filter((chItem) => !chItem.hidden)
            .filter((childItem) => !childItem.permission || hasPermission(permissions, childItem.permission, 'READ'));
    };

    return (
        <>
            {NavItems
                .filter((item) => {
                    if (item.hidden) return false;

                    if (item.children?.length) {
                        return hasVisibleChildren(item.children);
                    }

                    return !item.permission || hasPermission(permissions, item.permission, 'READ');
                })
                .map((item, index) => {
                    const visibleChildren = item.children ? getVisibleChildren(item.children) : [];
                    const hasChildren = visibleChildren.length > 0;
                    const groupActive = hasChildren && isGroupActive(pathname, visibleChildren);

                    if (hasChildren) {
                        return (
                            <NavGroup
                                key={item.id ?? index}
                                item={item}
                                visibleChildren={visibleChildren}
                                isNavOpen={expanded}
                                onNavigate={handleLinkClick}
                                groupActive={groupActive}
                            />
                        );
                    }

                    if (!item.route) {
                        return null;
                    }

                    return (
                        <NavLeaf
                            key={item.id ?? index}
                            item={item}
                            isNavOpen={expanded}
                            onNavigate={handleLinkClick}
                        />
                    );
                })
                .filter(Boolean)}
        </>
    );
});

export default NavbarListView;
